if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        // Guard native submissions as well as AJAX while JS selection refreshes are active.
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }
    }

    getSelectionSnapshot() {
      if (!this.form.isConnected) return null;
      const id = this.form.querySelector('[name="id"]')?.value;
      if (!id) return null;
      const state = this.form.spkVariantSelection;
      if (!state) {
        const scope = this.closest('.js-quickbuy, .cc-featured-product, .shopify-section');
        const picker = scope && [...scope.querySelectorAll('variant-picker[data-product-form-id]')]
          .find((element) => element.dataset.productFormId === this.form.id);
        // A visible picker must finish initialization before its form can submit.
        return picker ? null : { id, revision: null };
      }
      const picker = state.picker;
      const Picker = customElements.get('variant-picker');
      if (state.pending || state.error || !picker?.isConnected || !Picker
        || picker.getProductForm() !== this.form || !state.variant?.available
        || String(state.variant.id) !== id
        || !Picker.sameOptions(state.optionIds, picker.getSelectedOptionIds())
        || picker.getSelectedOptions().includes(null)
        || !Picker.sameOptions(state.variant.options, picker.getSelectedOptions())) return null;
      return { id, revision: state.revision };
    }

    setPurchaseBusy(busy) {
      this.form.spkCartSubmitting = busy;
      const picker = this.form.spkVariantSelection?.picker;
      if (picker?.isConnected) picker.updateAddToCartButton();
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      this.submitBtn = this.querySelector('[name="add"]');
      const selection = this.getSelectionSnapshot();
      if (!selection || !this.submitBtn || this.submitBtn.disabled || this.form.spkCartSubmitting
        || this.submitBtn.getAttribute('aria-disabled') === 'true') {
        evt.preventDefault();
        const state = this.form.spkVariantSelection;
        if (!selection && state?.variant?.available && !state.pending && !state.error
          && !state.picker.getSelectedOptions().includes(null)) {
          state.revision += 1;
          state.error = true;
        }
        state?.picker?.updateAddToCartButton();
        return;
      }
      if (theme.settings.afterAtc === 'no-js') return;
      evt.preventDefault();

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');
      this.setPurchaseBusy(true);

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      let added = false;
      let postingPicker;
      let pickerWasInert = false;
      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        // Selection can change while the cart pre-read is in flight. Never send its old ID.
        const latest = this.getSelectionSnapshot();
        if (!latest || latest.id !== selection.id || latest.revision !== selection.revision) {
          this.setErrorMsgState(this.form.spkVariantSelection?.picker?.dataset.changedText
            || theme.strings.noSelectedVariant);
          return;
        }
        const formData = new FormData(this.form);
        if (String(formData.get('id')) !== latest.id) {
          this.setErrorMsgState(this.form.spkVariantSelection?.picker?.dataset.changedText
            || theme.strings.noSelectedVariant);
          return;
        }
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);
        const fetchRequestOpts = {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        };
        // Keep the deliberate selection visible and unchanged during the actual cart commit.
        postingPicker = this.form.spkVariantSelection?.picker;
        if (postingPicker) {
          pickerWasInert = postingPicker.hasAttribute('inert');
          postingPicker.setAttribute('inert', '');
        }
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);
        added = true;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        if (postingPicker) postingPicker.toggleAttribute('inert', pickerWasInert);
        // Reconcile with the current picker/button, which may have been replaced while awaiting the cart.
        this.setPurchaseBusy(false);
        const currentButton = this.querySelector('[name="add"]');
        this.submitBtn.removeAttribute('aria-disabled');
        currentButton?.removeAttribute('aria-disabled');
        if (added) currentButton?.classList.add('is-success');
        this.submitBtn.classList.remove('is-loading');
        if (!added) currentButton?.classList.remove('is-loading');
        setTimeout(() => {
          currentButton?.classList.remove('is-loading');
          currentButton?.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
